import { defineStore } from 'pinia';
import { getDashboardNewContacted, getDashboardNewQualifications, getDashboardNewNotesUnsigned, getDashboardNewNotesSigned, getDashboardNewSigned, getDashboardCandidatesProcesses, getTopPerformersCandidatesProcesses, getDashboardCandidatesMultiposting } from '@/api/dashboard';
type State = {
  statistics: Record<string, {
    total: number;
    value: number;
    topPerformers: {
      _id: string;
      firstName: string;
      lastName: string;
      value: number;
    }[];
    isLoading: boolean;
  }>;
  processes: {
    isLoading: boolean;
    kpis: {
      _id: string;
      stepName: string;
      topPerformers: {
        _id: string;
        firstName: string;
        lastName: string;
        value: number;
      }[];
      isLoading: boolean;
    }[];
  };
  multidiffusionStatistics: Record<string, {
    total: number;
    value: number;
    topPerformers: {
      _id: string;
      firstName: string;
      lastName: string;
      value: number;
    }[];
    isLoading: boolean;
  }>;
  coachId: string | null;
  period: string | null;
  startDate: string | null;
  endDate: string | null;
  fetchedAt: Date | null;
  organizationId: string | null;
};

export const useStatisticsStore = defineStore('statistics', {
  state: (): State => {
    return {
      statistics: {
        contactedCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        candidatesQualifications: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        notesUnsigned: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        notesSigned: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        signed: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
      },
      processes: {
        isLoading: false,
        kpis: [],
      },
      multidiffusionStatistics: {
        nbCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        nbAcceptedCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        nbWaitingCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        nbRejectedCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        nbRecruitedCandidates: {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        },
        candidatesOrigins: {},
      },
      coachId: null,
      period: null,
      startDate: null,
      endDate: null,
      fetchedAt: null,
      organizationId: null,
    };
  },
  getters: {
    isLoading: (state) => {
      return state.processes.isLoading || Object.values(state.statistics).some((stat) => stat.isLoading) || Object.values(state.multidiffusionStatistics).some((stat) => stat.isLoading);
    },
  },
  actions: {
    setAllLoading(isLoading: boolean) {
      Object.keys(this.statistics).forEach((key) => {
        this.statistics[key].isLoading = isLoading;
      });

      Object.keys(this.multidiffusionStatistics).forEach((key) => {
        this.multidiffusionStatistics[key].isLoading = isLoading;
      });

      this.processes.isLoading = isLoading;
    },
    resetAllStatistics() {
      Object.keys(this.statistics).forEach((key) => {
        this.statistics[key] = {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        };
      });

      Object.keys(this.multidiffusionStatistics).forEach((key) => {
        this.multidiffusionStatistics[key] = {
          total: 0,
          value: 0,
          topPerformers: [],
          isLoading: false,
        };
      });

      this.processes.kpis = [];
    },
    async getStatistics(params: {
      coachId: string | null;
      period: string | null;
      startDate: string | null;
      endDate: string | null;
    }) {
      this.coachId = params.coachId;
      this.period = params.period;
      this.startDate = params.startDate;
      this.endDate = params.endDate;

      await this.getKPIs();

      this.fetchedAt = new Date();
    },
    async getKPIs() {
      this.resetAllStatistics();
      this.setAllLoading(true);

      await this.getContactedCandidates();
      await this.getQualificationsCandidates();

      await this.getNotesUnsigned();
      await this.getNotesSigned();
      await this.getSigned();

      await this.getProcesses();

      await this.getDashboardCandidatesMultiposting();

      this.setAllLoading(false);
    },
    async getDashboardCandidatesMultiposting() {
      const { data } = await getDashboardCandidatesMultiposting({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.multidiffusionStatistics.nbCandidates = {
        total: data.nbCandidates,
        value: data.nbCandidates,
        topPerformers: [],
        isLoading: false,
      };
      this.multidiffusionStatistics.nbAcceptedCandidates = {
        total: data.nbAcceptedCandidates,
        value: data.nbAcceptedCandidates,
        topPerformers: [],
        isLoading: false,
      };
      this.multidiffusionStatistics.nbWaitingCandidates = {
        total: data.nbWaitingCandidates,
        value: data.nbWaitingCandidates,
        topPerformers: [],
        isLoading: false,
      };
      this.multidiffusionStatistics.nbRejectedCandidates = {
        total: data.nbRejectedCandidates,
        value: data.nbRejectedCandidates,
        topPerformers: [],
        isLoading: false,
      };
      this.multidiffusionStatistics.nbRecruitedCandidates = {
        total: data.nbRecruitedCandidates,
        value: data.nbRecruitedCandidates,
        topPerformers: [],
        isLoading: false,
      };
      this.multidiffusionStatistics.candidatesOrigins = data.candidatesOrigins;
    },
    async getProcesses() {
      this.processes.isLoading = true;
      this.processes.kpis = [];

      const processes = await getDashboardCandidatesProcesses({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      const topPerformers = await getTopPerformersCandidatesProcesses({
        start: this.startDate,
        end: this.endDate,
      });

      const formattedProcesses = processes?.data?.map((process: any) => ({
        ...process,
        name: process.stepName,
        topPerformers: topPerformers?.data?.find((topPerformer: any) => topPerformer.processId === process._id)?.topPerformers || [],
      }));

      this.processes.kpis = formattedProcesses;

      this.processes.isLoading = false;
    },
    async getContactedCandidates() {
      const { data } = await getDashboardNewContacted({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.statistics.contactedCandidates = {
        total: data.total,
        value: data.value,
        topPerformers: data.topPerformers,
        isLoading: false,
      };
    },
    async getQualificationsCandidates() {
      const { data } = await getDashboardNewQualifications({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.statistics.candidatesQualifications = {
        total: data.total,
        value: data.value,
        topPerformers: data.topPerformers,
        isLoading: false,
      };
    },
    async getNotesUnsigned() {
      const { data } = await getDashboardNewNotesUnsigned({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.statistics.notesUnsigned = {
        total: data.total,
        value: data.value,
        topPerformers: data.topPerformers,
        isLoading: false,
      };
    },
    async getNotesSigned() {
      const { data } = await getDashboardNewNotesSigned({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.statistics.notesSigned = {
        total: data.total,
        value: data.value,
        topPerformers: data.topPerformers,
        isLoading: false,
      };
    },
    async getSigned() {
      const { data } = await getDashboardNewSigned({
        start: this.startDate,
        end: this.endDate,
        coachId: this.coachId,
      });

      this.statistics.signed = {
        total: data.total,
        value: data.value,
        topPerformers: data.topPerformers,
        isLoading: false,
      };
    },
    setOrganizationId(organizationId: string) {
      this.organizationId = organizationId;
    },
  },
  persist: true,
});
